import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, Checkbox, Chip, Divider, Drawer, FormControlLabel, Grid, IconButton,
  Link as MLink, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreatePromptTemplateMutation, useCreateMasteryWorkflowMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ABA } from "./abaDesign";
// Batch 6 — the hierarchy branch renders the new shared modal instead of
// the drawer. Workflow branch continues to render the drawer (Batch 7).
import HierarchyQuickModal from "./HierarchyQuickModal";
// Batch 7 — the workflow branch now delegates to WorkflowQuickModal.
// Follow-up `batch-7b-drop-QuickHierarchyDrawer` deletes this file
// once both invocation contexts are proven in the demo build.
import WorkflowQuickModal from "./WorkflowQuickModal";

/**
 * Batch 37 — In-page drawer for Prompt Levels or Workflow.
 *
 * Full editing parity with the standalone editor: drag reorder, keyboard
 * up/down, duplicate, delete, reverse, live Prompting Ladder preview,
 * Independence Score / Level Colour / Counts as Independent / Active,
 * Save Draft / Save and Activate.
 */
type Kind = "hierarchy" | "workflow";

type Level = {
  levelName: string; shortCode: string; description: string; score: number;
  colour: string; countsAsIndependent: boolean; isActive: boolean;
};
const emptyLevel = (): Level => ({
  levelName: "", shortCode: "", description: "", score: 50, colour: "#1976d2",
  countsAsIndependent: false, isActive: true,
});

type Phase = {
  phaseName: string; colour: string; description: string;
  entryRule: string; advancementRule: string; regressionRule: string;
  transitionsTo: string;
  minPercentCorrect: number; acrossSessions: number; minTrialsPerSession: number;
  promptRequirement: string; manualApprovalRequired: boolean;
  isStarting: boolean; isTerminal: boolean; isActive: boolean;
};
const emptyPhase = (): Phase => ({
  phaseName: "", colour: "#1976d2", description: "",
  entryRule: "", advancementRule: "", regressionRule: "",
  transitionsTo: "",
  minPercentCorrect: 80, acrossSessions: 3, minTrialsPerSession: 5,
  promptRequirement: "any", manualApprovalRequired: false,
  isStarting: false, isTerminal: false, isActive: true,
});

// Standard ABA hierarchy for Most-to-Least order. Full Physical (most support)
// first through Independent (least support). Visual is included between Verbal
// and Independent.
const STARTER_LEVELS: Level[] = [
  { levelName: "Full Physical",    shortCode: "FP", description: "Hand-over-hand support through the full response.", score: 10, colour: "#c62828", countsAsIndependent: false, isActive: true },
  { levelName: "Partial Physical", shortCode: "PP", description: "Physical support at the elbow / shoulder only.",     score: 25, colour: "#ef6c00", countsAsIndependent: false, isActive: true },
  { levelName: "Model",            shortCode: "M",  description: "Demonstrate the response for the learner to imitate.", score: 40, colour: "#f9a825", countsAsIndependent: false, isActive: true },
  { levelName: "Gestural",         shortCode: "G",  description: "Point / gesture toward the correct response.",       score: 55, colour: "#9e9d24", countsAsIndependent: false, isActive: true },
  { levelName: "Verbal",           shortCode: "V",  description: "Verbal cue that names or hints at the response.",    score: 70, colour: "#2e7d32", countsAsIndependent: false, isActive: true },
  { levelName: "Visual",           shortCode: "Vi", description: "Visual cue (image, text, arrow, model).",            score: 85, colour: "#0277bd", countsAsIndependent: false, isActive: true },
  { levelName: "Independent",      shortCode: "I",  description: "Learner responds without any prompt.",               score: 100, colour: "#1565c0", countsAsIndependent: true,  isActive: true },
];

// Standard ABA workflow. Five phases: Baseline → Acquisition → Generalization
// → Mastered → Maintenance. Must match the full editor's default.
const STARTER_PHASES: Phase[] = [
  { ...emptyPhase(), phaseName: "Baseline",       isStarting: true,  isTerminal: false, minPercentCorrect: 0,   acrossSessions: 1, minTrialsPerSession: 3, transitionsTo: "Acquisition",    colour: "#9e9e9e", advancementRule: "Move to Acquisition after 1 baseline session" },
  { ...emptyPhase(), phaseName: "Acquisition",    isStarting: false, isTerminal: false, minPercentCorrect: 80,  acrossSessions: 3, minTrialsPerSession: 5, transitionsTo: "Generalization", colour: "#1976d2", advancementRule: "≥80% correct across 3 sessions" },
  { ...emptyPhase(), phaseName: "Generalization", isStarting: false, isTerminal: false, minPercentCorrect: 90,  acrossSessions: 2, minTrialsPerSession: 5, transitionsTo: "Mastered",       colour: "#ff8f00", advancementRule: "≥90% independent across 2 sessions" },
  { ...emptyPhase(), phaseName: "Mastered",       isStarting: false, isTerminal: true,  minPercentCorrect: 100, acrossSessions: 1, minTrialsPerSession: 0, transitionsTo: "Maintenance",    colour: "#2e7d32", advancementRule: "Automatic move to Maintenance on next session" },
  { ...emptyPhase(), phaseName: "Maintenance",    isStarting: false, isTerminal: true,  minPercentCorrect: 80,  acrossSessions: 1, minTrialsPerSession: 3, transitionsTo: "",               colour: "#6a1b9a", advancementRule: "Periodic maintenance probes" },
];

const orderHint = (direction: string) =>
  direction === "custom" ? "Drag Prompt Levels into the exact order Staff should follow."
  : direction === "least_to_most" ? "Arrange Levels from the least intrusive support to the most support."
  : "Arrange Levels from the most support to the least support.";

export default function QuickHierarchyDrawer(props: {
  open: boolean; kind: Kind;
  onClose: () => void;
  onCreated: (kind: Kind, id: number) => void;
}) {
  const { open, kind, onClose, onCreated } = props;
  // NOTE — Batch 6 originally placed early returns for the hierarchy
  // and workflow kinds BEFORE the hook block below, which trips
  // react-hooks/rules-of-hooks (a compile-blocking rule in CRA).
  // Hooks now run unconditionally; the branch selection happens at
  // render time via the `redirect` value below. Behaviour is
  // unchanged — same modal for each kind, same props threaded.
  const redirect =
    (kind as string) === "hierarchy"
      ? <HierarchyQuickModal open={open} onClose={onClose} onCreated={onCreated} />
      : (kind as string) === "workflow"
        ? <WorkflowQuickModal open={open} onClose={onClose} onCreated={onCreated} />
        : null;

  const { showMessage, showErrorMessage } = useNotifier();
  const [createHierarchy, { isLoading: hSaving }] = useCreatePromptTemplateMutation();
  const [createWorkflow, { isLoading: wSaving }] = useCreateMasteryWorkflowMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [direction, setDirection] = useState("most_to_least");
  const [levels, setLevels] = useState<Level[]>(STARTER_LEVELS);
  const [phases, setPhases] = useState<Phase[]>(STARTER_PHASES);
  const [autoProg, setAutoProg] = useState(true);
  const [manualOverride, setManualOverride] = useState(true);
  const [supervisorApproval, setSupervisorApproval] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // Batch 48 — Workflow drawer accordion state. Only the first phase is
  // expanded by default so administrators aren't overwhelmed with five
  // dense forms stacked from top to bottom.
  const [expandedPhaseIdx, setExpandedPhaseIdx] = useState<number>(0);
  // Show / hide the Move Back sub-card per phase (persisted only for the
  // life of the drawer). The default is "off" for phases that have no
  // regression rule, matching the spec's "make it optional and collapsible".
  const [regressionOn, setRegressionOn] = useState<Record<number, boolean>>({});

  const reset = () => {
    setName(""); setDescription(""); setIsDefault(false); setDirection("most_to_least");
    setLevels(STARTER_LEVELS); setPhases(STARTER_PHASES);
    setAutoProg(true); setManualOverride(true); setSupervisorApproval(false);
  };
  const close = () => { reset(); onClose(); };

  // ----- level operations (drag + keyboard) -----
  const moveLevel = (from: number, to: number) => setLevels((L) => {
    if (to < 0 || to >= L.length) return L;
    const c = [...L]; const [x] = c.splice(from, 1); c.splice(to, 0, x); return c;
  });
  const duplicateLevel = (i: number) => setLevels((L) => {
    const c = [...L]; c.splice(i + 1, 0, { ...c[i], levelName: c[i].levelName + " (copy)" }); return c;
  });
  const removeLevel = (i: number) => setLevels((L) => L.length > 1 ? L.filter((_, k) => k !== i) : L);
  const reverseLevels = () => setLevels((L) => [...L].reverse());
  const addLevel = () => setLevels((L) => [...L, emptyLevel()]);

  // ----- phase operations -----
  const movePhase = (from: number, to: number) => setPhases((P) => {
    if (to < 0 || to >= P.length) return P;
    const c = [...P]; const [x] = c.splice(from, 1); c.splice(to, 0, x); return c;
  });
  const duplicatePhase = (i: number) => setPhases((P) => {
    const c = [...P]; c.splice(i + 1, 0, { ...c[i], phaseName: c[i].phaseName + " (copy)", isStarting: false }); return c;
  });
  const removePhase = (i: number) => setPhases((P) => P.length > 1 ? P.filter((_, k) => k !== i) : P);
  const addPhase = () => setPhases((P) => [...P, emptyPhase()]);

  const validateHierarchy = (): string | null => {
    if (!name.trim()) return "Please enter a Prompt Levels Name.";
    const active = levels.filter((l) => l.isActive && l.levelName.trim());
    if (active.length < 2) return "An active template needs at least 2 active levels.";
    const names = active.map((l) => l.levelName.trim().toLowerCase());
    if (new Set(names).size !== names.length) return "Level names must be unique.";
    return null;
  };
  const validateWorkflow = (): string | null => {
    if (!name.trim()) return "Please enter a Workflow Name.";
    const active = phases.filter((p) => p.isActive && p.phaseName.trim());
    if (active.length > 1) {
      const both = active.find((p) => p.isStarting && p.isTerminal);
      if (both) return `Phase "${both.phaseName}" cannot be Starting Phase and Final Phase at the same time.`;
    }
    if (active.filter((p) => p.isStarting).length !== 1) return "Exactly one phase must be marked as the starting phase.";
    if (active.filter((p) => p.isTerminal).length < 1) return "At least one phase must be marked as terminal.";
    const names = active.map((p) => p.phaseName.trim().toLowerCase());
    if (new Set(names).size !== names.length) return "Phase names must be unique.";
    // Batch 32 §5.1 — circular progression detection, naming both ends.
    const map: Record<string, string> = {};
    active.forEach((p) => { map[p.phaseName.trim()] = (p.transitionsTo || "").trim(); });
    for (const start of Object.keys(map)) {
      const seen = new Set<string>(); let cur: string | undefined = start; let prev = ""; let steps = 0;
      while (cur && steps++ < Object.keys(map).length + 1) {
        if (seen.has(cur)) return `Circular progression: "${prev}" transitions to "${cur}", which loops back into the flow.`;
        seen.add(cur);
        const next: string = map[cur] ?? "";
        if (!next) break;
        if (!(next in map)) return `Phase "${cur}" transitions to unknown phase "${next}".`;
        prev = cur; cur = next;
      }
    }
    for (const p of phases) {
      if (p.minPercentCorrect < 0 || p.minPercentCorrect > 100) return `${p.phaseName || "phase"} accuracy must be 0–100%.`;
    }
    return null;
  };

  const submit = async (asDraft: boolean) => {
    const err = kind === "hierarchy" ? validateHierarchy() : validateWorkflow();
    if (err) { showErrorMessage(err); return; }
    try {
      if (kind === "hierarchy") {
        const res: any = await createHierarchy({
          templateName: name.trim(), description, direction, isDefault,
          status: asDraft ? "draft" : "active",
          levels: levels.filter((l) => l.levelName.trim()).map((l, i) => ({ ...l, orderNumber: i })),
        }).unwrap();
        if (res?.status && res?.data?.id) {
          showMessage("Prompt Levels saved.");
          onCreated("hierarchy", Number(res.data.id));
          close();
        } else showErrorMessage(res?.message || "Failed to save prompt level template.");
      } else {
        const res: any = await createWorkflow({
          workflowName: name.trim(), description, isDefault,
          scope: "organization",
          status: asDraft ? "draft" : "active",
          automaticProgression: autoProg, manualOverrideAllowed: manualOverride,
          supervisorApprovalRequired: supervisorApproval,
          phases: phases.filter((p) => p.phaseName.trim()).map((p, i) => ({ ...p, orderNumber: i })),
        }).unwrap();
        if (res?.status && res?.data?.id) {
          showMessage("Workflow saved.");
          onCreated("workflow", Number(res.data.id));
          close();
        } else showErrorMessage(res?.message || "Failed to save workflow.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed.");
    }
  };

  const busy = hSaving || wSaving;
  const title = kind === "hierarchy" ? "Create Prompt Levels" : "Create Workflow";
  const activeLevels = useMemo(() => levels.filter((l) => l.isActive && l.levelName.trim()), [levels]);
  const activePhases = useMemo(() => phases.filter((p) => p.isActive && p.phaseName.trim()), [phases]);

  if (redirect) return redirect;
  return (
    <Drawer anchor="right" open={open} onClose={close}
      PaperProps={{ sx: {
        width: { xs: "100%", sm: "92vw", md: kind === "workflow" ? "min(75vw, 1080px)" : 900 },
        minWidth: { md: kind === "workflow" ? 880 : undefined },
      } }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider" }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">Your Program draft will remain open.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <MLink href={kind === "hierarchy" ? "/aba-therapy/prompt-hierarchies/new" : "/aba-therapy/workflows/new"}
            target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 12, display: "inline-flex", alignItems: "center" }}>
            <OpenInNewIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Open in new tab
          </MLink>
          <IconButton onClick={close}><CloseIcon /></IconButton>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5, md: ABA.drawerContentPadding }, py: 2, pb: 12, overflowY: "auto" }} data-testid="quick-drawer-body">
        <Stack spacing={1.5}>
          <TextField size="small" fullWidth
            label={kind === "hierarchy" ? "Prompt Levels Name *" : "Workflow Name *"}
            value={name} onChange={(e) => setName(e.target.value)} />
          <TextField size="small" fullWidth multiline minRows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />} label="Use as default" />
            {kind === "workflow" && <>
              <FormControlLabel control={<Checkbox checked={autoProg} onChange={(e) => setAutoProg(e.target.checked)} />} label="Automatic Progression" />
              <FormControlLabel control={<Checkbox checked={manualOverride} onChange={(e) => setManualOverride(e.target.checked)} />} label="Manual Override Allowed" />
              <FormControlLabel control={<Checkbox checked={supervisorApproval} onChange={(e) => setSupervisorApproval(e.target.checked)} />} label="Supervisor Approval Required" />
            </>}
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {kind === "hierarchy" ? (
          <>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" fontWeight={700}>Prompt Levels</Typography>
                <Typography variant="caption" color="text.secondary">{orderHint(direction)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select size="small" fullWidth value={direction} onChange={(e) => setDirection(e.target.value)}>
                  <MenuItem value="most_to_least">Most-to-Least</MenuItem>
                  <MenuItem value="least_to_most">Least-to-Most</MenuItem>
                  <MenuItem value="custom">Custom Order</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip title="Reverse order"><Button size="small" startIcon={<SwapVertIcon />} onClick={reverseLevels}>Reverse</Button></Tooltip>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addLevel}>Add Level</Button>
                </Stack>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Stack spacing={1} data-testid="prompt-levels-list">
                  {levels.map((lv, i) => (
                    <Card key={i} variant="outlined"
                      draggable onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== i) moveLevel(dragIdx, i); setDragIdx(null); }}
                      sx={{ p: 1.25, borderLeft: `4px solid ${lv.colour}`, opacity: lv.isActive ? 1 : 0.6, cursor: "default" }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs="auto">
                          <Tooltip title="Drag to reorder">
                            <IconButton size="small" sx={{ cursor: "grab" }}><DragIndicatorIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Typography component="span" variant="caption" sx={{ fontWeight: 700, ml: 0.5 }}>{i + 1}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField size="small" fullWidth label="Level Name"
                            value={lv.levelName} onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, levelName: e.target.value } : x))} />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField size="small" fullWidth label="Short Code"
                            value={lv.shortCode} onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, shortCode: e.target.value } : x))} />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField size="small" fullWidth type="color" label="Level Colour" InputLabelProps={{ shrink: true }}
                            value={lv.colour} onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, colour: e.target.value } : x))} />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <Tooltip title="0–100 score used in reports. Higher = more independent.">
                            <TextField size="small" fullWidth type="number" label="Independence Score"
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              value={lv.score} onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, score: Math.min(100, Math.max(0, Number(e.target.value))) } : x))} />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField size="small" fullWidth label="Description"
                            value={lv.description} onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, description: e.target.value } : x))} />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                            <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={lv.countsAsIndependent}
                              onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, countsAsIndependent: e.target.checked } : x))} />}
                              label={<Typography variant="caption">Counts as Independent</Typography>} />
                            <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={lv.isActive}
                              onChange={(e) => setLevels((L) => L.map((x, k) => k === i ? { ...x, isActive: e.target.checked } : x))} />}
                              label={<Typography variant="caption">Active</Typography>} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Tooltip title="Move up"><span><IconButton size="small" aria-label="move-up" onClick={() => moveLevel(i, i - 1)} disabled={i === 0}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                            <Tooltip title="Move down"><span><IconButton size="small" aria-label="move-down" onClick={() => moveLevel(i, i + 1)} disabled={i === levels.length - 1}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                            <Tooltip title="Duplicate"><IconButton size="small" onClick={() => duplicateLevel(i)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><span><IconButton size="small" onClick={() => removeLevel(i)} disabled={levels.length === 1}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ p: 1.5, position: "sticky", top: 8 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Prompting Ladder</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    {direction === "least_to_most" ? "Least Support → Most Support" : direction === "custom" ? "Custom order" : "Most Support → Least Support"}
                  </Typography>
                  {activeLevels.length === 0 ? (
                    <Alert severity="info" sx={{ fontSize: 12 }}>Add active levels to see the ladder.</Alert>
                  ) : (
                    <Stack spacing={0.5}>
                      {activeLevels.map((l, i) => (
                        <Box key={i} sx={{ p: 1, borderRadius: 1, backgroundColor: alpha(l.colour, 0.1), borderLeft: `3px solid ${l.colour}`, display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, width: 20 }}>{i + 1}</Typography>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>{l.levelName}</Typography>
                          {l.shortCode && <Typography variant="caption" color="text.secondary">[{l.shortCode}]</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          <WorkflowEditor
            phases={phases}
            setPhases={setPhases}
            expandedPhaseIdx={expandedPhaseIdx}
            setExpandedPhaseIdx={setExpandedPhaseIdx}
            regressionOn={regressionOn}
            setRegressionOn={setRegressionOn}
            movePhase={movePhase}
            duplicatePhase={duplicatePhase}
            removePhase={removePhase}
            addPhase={addPhase}
          />
        )}
      </Box>

      {/* Sticky footer — leaves room for chat widget below via marginRight/marginBottom */}
      <Box sx={{ position: "sticky", bottom: 0, p: 2, pb: { xs: 10, md: 3 },
        borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        {(() => {
          const err = kind === "hierarchy" ? validateHierarchy() : validateWorkflow();
          return (
            <>
              {err && (
                <Typography variant="caption" color="error.main" sx={{ display: "block", textAlign: "right", mb: 0.5 }}
                  data-testid="quickdrawer-save-blocker">
                  Cannot save & activate: {err}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={close}>Cancel</Button>
                <Button variant="outlined" onClick={() => submit(true)} disabled={busy || !name.trim()}>Save Draft</Button>
                <Button variant="contained" onClick={() => submit(false)} disabled={busy || !!err}
                  data-testid="quickdrawer-save-activate">
                  {busy ? "Saving…" : "Save and Activate"}
                </Button>
              </Box>
            </>
          );
        })()}
      </Box>
    </Drawer>
  );
}

// ─── Batch 48 — premium workflow editor ─────────────────────────────────

function ruleSummary(p: Phase): string {
  const trials = p.minTrialsPerSession;
  const sessions = p.acrossSessions;
  const to = p.transitionsTo ? `to ${p.transitionsTo}` : "forward";
  return `Move ${to} after at least ${sessions} consecutive session${sessions === 1 ? "" : "s"} with ${p.minPercentCorrect}% or higher accuracy and at least ${trials} trial${trials === 1 ? "" : "s"} per session.`;
}

function regressionSummary(p: Phase): string | null {
  if (!p.regressionRule && !p.minPercentCorrect) return null;
  return p.regressionRule || null;
}

function WorkflowEditor({
  phases, setPhases,
  expandedPhaseIdx, setExpandedPhaseIdx,
  regressionOn, setRegressionOn,
  movePhase, duplicatePhase, removePhase, addPhase,
}: {
  phases: Phase[]; setPhases: React.Dispatch<React.SetStateAction<Phase[]>>;
  expandedPhaseIdx: number; setExpandedPhaseIdx: (n: number) => void;
  regressionOn: Record<number, boolean>; setRegressionOn: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  movePhase: (from: number, to: number) => void;
  duplicatePhase: (i: number) => void;
  removePhase: (i: number) => void;
  addPhase: () => void;
}) {
  const activeNames = phases.filter((p) => p.isActive && p.phaseName.trim()).map((p) => p.phaseName.trim());

  const setPhaseField = (i: number, patch: Partial<Phase>) =>
    setPhases((P) => P.map((x, k) => k === i ? { ...x, ...patch } : x));

  // Enforce "one Starting Phase" when the flag is toggled on.
  const setStartingOn = (i: number) =>
    setPhases((P) => P.map((x, k) => ({ ...x, isStarting: k === i })));

  return (
    <Box data-testid="workflow-editor">
      {/* Visual workflow preview — Batch 33 E: only phases with a name AND
         isActive appear in the flow strip. Unnamed/inactive phases would
         otherwise render as "Phase N" placeholders with a dangling arrow off
         the end. A footer caption tells the user how many rows are hidden. */}
      <Card variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.02) }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Workflow Flow</Typography>
        {(() => {
          const flowPhases = phases.filter((p) => p.isActive && p.phaseName.trim());
          const hiddenCount = phases.length - flowPhases.length;
          if (flowPhases.length === 0) {
            return (
              <Typography variant="caption" color="text.secondary">
                Name at least one active phase to preview the flow.
              </Typography>
            );
          }
          return (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "stretch" }}
                flexWrap="wrap" useFlexGap data-testid="workflow-flow">
                {flowPhases.map((p, fi) => {
                  const originalIdx = phases.indexOf(p);
                  const isCurrent = originalIdx === expandedPhaseIdx;
                  return (
                    <React.Fragment key={originalIdx}>
                      <Card variant="outlined" onClick={() => setExpandedPhaseIdx(originalIdx)}
                        sx={{ p: 1, minWidth: 150, cursor: "pointer", flex: 1,
                          borderLeft: `4px solid ${p.colour}`,
                          borderWidth: isCurrent ? 2 : 1,
                          borderColor: isCurrent ? "primary.main" : "divider",
                          bgcolor: isCurrent ? (t) => alpha(t.palette.primary.main, 0.05) : "background.paper",
                        }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{p.phaseName}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          {p.isStarting && <Chip size="small" color="primary" variant="outlined" label="Start" />}
                          {p.isTerminal && <Chip size="small" color="success" variant="outlined" label="Final" />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          {p.minPercentCorrect}%+ across {p.acrossSessions} · {p.minTrialsPerSession} trial{p.minTrialsPerSession === 1 ? "" : "s"}
                        </Typography>
                      </Card>
                      {fi < flowPhases.length - 1 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 0.5, color: "text.disabled" }}>→</Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </Stack>
              {hiddenCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {hiddenCount} phase{hiddenCount === 1 ? " is" : "s are"} hidden from the flow (no name, or inactive).
                </Typography>
              )}
            </>
          );
        })()}
      </Card>

      {/* Phase toolbar */}
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
        <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>Phases</Typography>
        <Button size="small" onClick={() => setExpandedPhaseIdx(-1)}>Collapse All</Button>
        <Button size="small" onClick={() => setExpandedPhaseIdx(-2)}>Expand All</Button>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addPhase}>Add Phase</Button>
      </Stack>

      <Stack spacing={1} data-testid="phases-list">
        {phases.map((p, i) => {
          const expanded = expandedPhaseIdx === i || expandedPhaseIdx === -2;
          const regEnabled = regressionOn[i] ?? !!p.regressionRule;
          return (
            <Card key={i} variant="outlined" sx={{ borderLeft: `4px solid ${p.colour}`, opacity: p.isActive ? 1 : 0.6 }}>
              {/* Collapsed / summary header */}
              <Box sx={{ p: 1.25, cursor: "pointer" }} onClick={() => setExpandedPhaseIdx(expanded ? -1 : i)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 22 }}>{i + 1}</Typography>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: p.colour }} />
                  <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1, fontStyle: p.phaseName.trim() ? "normal" : "italic", color: p.phaseName.trim() ? "text.primary" : "text.disabled" }}>
                    {p.phaseName.trim() || "Unnamed phase — set a name to include in the flow"}
                  </Typography>
                  {p.isStarting && <Chip size="small" color="primary" variant="outlined" label="Start" />}
                  {p.isTerminal && <Chip size="small" color="success" variant="outlined" label="Final" />}
                  <Chip size="small" variant="outlined" label={p.isActive ? "Active" : "Inactive"} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, pl: 4 }}>
                  {ruleSummary(p)}
                </Typography>
              </Box>

              {expanded && (
                <Box sx={{ p: 1.5, pt: 0 }}>
                  <Divider sx={{ mb: 1.5 }} />

                  {/* A. Phase Details */}
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>PHASE DETAILS</Typography>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={5}>
                      <TextField size="small" fullWidth label="Phase Name *"
                        value={p.phaseName}
                        onChange={(e) => setPhaseField(i, { phaseName: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField size="small" fullWidth label="Description"
                        value={p.description}
                        onChange={(e) => setPhaseField(i, { description: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField size="small" fullWidth type="color" label="Phase Colour" InputLabelProps={{ shrink: true }}
                        value={p.colour}
                        onChange={(e) => setPhaseField(i, { colour: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControlLabel control={<Switch size="small" checked={p.isStarting}
                          onChange={(e) => e.target.checked ? setStartingOn(i) : setPhaseField(i, { isStarting: false })} />}
                          label="Starting Phase" />
                        <FormControlLabel control={<Switch size="small" checked={p.isTerminal}
                          onChange={(e) => setPhaseField(i, { isTerminal: e.target.checked })} />}
                          label="Final Phase" />
                        <FormControlLabel control={<Switch size="small" checked={p.isActive}
                          onChange={(e) => setPhaseField(i, { isActive: e.target.checked })} />}
                          label="Active" />
                      </Stack>
                      {p.isStarting && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>This is where new Targets begin.</Typography>}
                      {p.isTerminal && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Targets may remain in this phase unless another transition is configured.</Typography>}
                    </Grid>
                  </Grid>

                  {/* B. Move Forward When — 12-col grid: 3/3/3/3 on lg */}
                  <Card variant="outlined" data-testid={`move-forward-${i}`} sx={{ p: ABA.cardPadding, mb: ABA.sectionGap, borderRadius: `${ABA.cardRadius}px`, bgcolor: (t) => alpha(t.palette.success.main, 0.04), borderColor: (t) => alpha(t.palette.success.main, 0.3) }}>
                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: "block", mb: 1 }}>MOVE FORWARD WHEN</Typography>
                    <Grid container spacing={ABA.gridSpacing} alignItems="flex-start">
                      <Grid item {...ABA.col.quarter} data-testid="ff-minacc">
                        <TextField size="small" fullWidth type="number" label="Minimum Accuracy"
                          InputProps={{ inputProps: { min: 0, max: 100 }, endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography> }}
                          value={p.minPercentCorrect}
                          onChange={(e) => setPhaseField(i, { minPercentCorrect: Math.min(100, Math.max(0, Number(e.target.value))) })}
                          helperText={p.minPercentCorrect > 0 && p.minPercentCorrect < 20
                            ? `Very low — did you mean ${p.minPercentCorrect * 10}%?` : undefined}
                          FormHelperTextProps={{ sx: { color: "warning.main", ml: 0 } }} />
                      </Grid>
                      <Grid item {...ABA.col.quarter}>
                        <TextField size="small" fullWidth type="number" label="Consecutive Sessions"
                          InputProps={{ inputProps: { min: 0 } }}
                          value={p.acrossSessions}
                          onChange={(e) => setPhaseField(i, { acrossSessions: Math.max(0, Number(e.target.value)) })} />
                      </Grid>
                      <Grid item {...ABA.col.quarter}>
                        <TextField size="small" fullWidth type="number" label="Minimum Trials per Session"
                          InputProps={{ inputProps: { min: 0 } }}
                          value={p.minTrialsPerSession}
                          onChange={(e) => setPhaseField(i, { minTrialsPerSession: Math.max(0, Number(e.target.value)) })} />
                      </Grid>
                      <Grid item {...ABA.col.quarter}>
                        <TextField select size="small" fullWidth label="Required Prompt Level"
                          value={p.promptRequirement || "any"}
                          onChange={(e) => setPhaseField(i, { promptRequirement: e.target.value })}>
                          <MenuItem value="any">Any prompt</MenuItem>
                          <MenuItem value="independent_only">Independent only</MenuItem>
                          <MenuItem value="custom">Specific Prompt Level</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item {...ABA.col.full}>
                        <FormControlLabel control={<Switch size="small" checked={p.manualApprovalRequired}
                          onChange={(e) => setPhaseField(i, { manualApprovalRequired: e.target.checked })} />}
                          label="Supervisor Approval Required" />
                      </Grid>
                    </Grid>
                  </Card>

                  {/* C. Next Phase — 4/8 split */}
                  <Card variant="outlined" data-testid={`next-phase-${i}`} sx={{ p: ABA.cardPadding, mb: ABA.sectionGap, borderRadius: `${ABA.cardRadius}px` }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>NEXT PHASE</Typography>
                    <Grid container spacing={ABA.gridSpacing} alignItems="flex-start">
                      <Grid item {...ABA.col.thirdEight}>
                        <TextField select size="small" fullWidth label="Next Phase"
                          value={p.transitionsTo || ""}
                          onChange={(e) => setPhaseField(i, { transitionsTo: e.target.value })}
                          SelectProps={{ displayEmpty: true }}
                          helperText=" ">
                          <MenuItem value=""><em>None (Final)</em></MenuItem>
                          {activeNames
                            .filter((n) => n !== p.phaseName.trim())
                            .map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item {...ABA.col.twoThirds}>
                        <TextField size="small" fullWidth label="Criteria to Move Forward"
                          helperText="Optional plain-language explanation used in clinical reports."
                          value={p.advancementRule}
                          onChange={(e) => setPhaseField(i, { advancementRule: e.target.value })} />
                      </Grid>
                    </Grid>
                  </Card>

                  {/* D. Move Back When (optional) */}
                  <Card variant="outlined" data-testid={`move-back-${i}`} sx={{ p: ABA.cardPadding, mb: ABA.sectionGap, borderRadius: `${ABA.cardRadius}px`,
                    bgcolor: (t) => alpha(t.palette.warning.main, 0.05),
                    borderColor: (t) => alpha(t.palette.warning.main, 0.3) }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="warning.dark" fontWeight={700}>MOVE BACK WHEN</Typography>
                      <FormControlLabel control={<Switch size="small" checked={regEnabled}
                        onChange={(e) => setRegressionOn((r) => ({ ...r, [i]: e.target.checked }))} />}
                        label={<Typography variant="caption">Enable regression rule</Typography>} />
                    </Stack>
                    {regEnabled && (
                      <Grid container spacing={ABA.gridSpacing} sx={{ mt: 0.5 }} alignItems="flex-start">
                        <Grid item {...ABA.col.third}>
                          <TextField size="small" fullWidth type="number" label="Regression Threshold"
                            InputProps={{ inputProps: { min: 0, max: 100 }, endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography> }}
                            value={0}
                            onChange={() => { /* Threshold is a plain-language field on this schema; captured in Criteria below. */ }} />
                        </Grid>
                        <Grid item {...ABA.col.third}>
                          <TextField size="small" fullWidth type="number" label="Consecutive Sessions"
                            InputProps={{ inputProps: { min: 0 } }}
                            value={p.acrossSessions}
                            onChange={(e) => setPhaseField(i, { acrossSessions: Math.max(0, Number(e.target.value)) })} />
                        </Grid>
                        <Grid item {...ABA.col.third}>
                          <TextField select size="small" fullWidth label="Previous Phase"
                            value={phases[i - 1]?.phaseName ?? ""}
                            onChange={(e) => setPhaseField(i, { regressionRule: `Move back to ${e.target.value} when regression thresholds are met.` })}
                            SelectProps={{ displayEmpty: true }}>
                            <MenuItem value=""><em>Select previous phase</em></MenuItem>
                            {activeNames.filter((n) => n !== p.phaseName.trim()).map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item {...ABA.col.full}>
                          <TextField size="small" fullWidth label="Criteria to Move Back"
                            helperText='e.g. "50% or below for 2 consecutive sessions"'
                            value={p.regressionRule}
                            onChange={(e) => setPhaseField(i, { regressionRule: e.target.value })} />
                        </Grid>
                      </Grid>
                    )}
                  </Card>

                  {/* Plain-language summary */}
                  <Card variant="outlined" sx={{ p: 1.25, bgcolor: (t) => alpha(t.palette.info.main, 0.05), borderColor: (t) => alpha(t.palette.info.main, 0.3) }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block" }}>SUMMARY</Typography>
                    <Typography variant="body2" data-testid={`phase-summary-${i}`}>{ruleSummary(p)}</Typography>
                    {regEnabled && regressionSummary(p) && (
                      <Typography variant="caption" color="warning.dark" sx={{ display: "block", mt: 0.5 }}>{regressionSummary(p)}</Typography>
                    )}
                  </Card>

                  {/* Row actions */}
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Tooltip title="Move up"><span><IconButton size="small" aria-label="move-up" onClick={() => movePhase(i, i - 1)} disabled={i === 0}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                    <Tooltip title="Move down"><span><IconButton size="small" aria-label="move-down" onClick={() => movePhase(i, i + 1)} disabled={i === phases.length - 1}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                    <Tooltip title="Duplicate"><IconButton size="small" onClick={() => duplicatePhase(i)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><span><IconButton size="small" onClick={() => removePhase(i)} disabled={phases.length === 1}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
                  </Stack>
                </Box>
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
